import { CustomerImpact } from '@canvas/operational-types';

import type { Site } from './sites-types';

export const urlStringFromHostname = (hostname: string) => `https://${hostname}`;

/**
 * ⚠️ IMPORTANT ⚠️
 * If you are adding another site here, ensure that it is added to the appropriate exclusion list
 * in the below statsig tenant lists. This is because we frequently face in-product-messaging issues where
 * a big banner blocks the entire page, breaking our pollinators and resulting in noise:
 * - Staging tenants: https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/segments/segment%3Aconfluence_whiteboards_pollinator_tenants_staging
 * - Prod tenants: https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/segments/segment%3Aconfluence_whiteboards_pollinator_tenants_prod
 *
 * These are ignored in the below statsig segment:
 * - https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/segments/segment%3Aconfluence_-_test_tenants_to_exclude_from_ipm
 *
 * See Slack thread for context: https://atlassian.slack.com/archives/C089VRNRJJF/p1759963056933259?thread_ts=1758160803.383379&cid=C089VRNRJJF
 */

/**
 * Maps a Site object to a URL string.
 *
 * @example
 *
 * ```
 * {
 *   hostname: 'hello.atlassian.net',
 *   cloudId: 'a436116f-02ce-4520-8fbb-7301462a1674',
 * }
 * ```
 *
 * becomes `hello.atlassian.net`
 */
export const urlStringFromSite = (site: Pick<Site, 'hostname'>) =>
  urlStringFromHostname(site.hostname);

export const productFabricSite = {
  hostname: 'product-fabric.atlassian.net',
  cloudId: 'DUMMY-158c8204-ff3b-47c2-adbb-a0906ccc722b',
  widestUsage: CustomerImpact.ATLASSIAN,
} satisfies Site;

export const helloSite = {
  hostname: 'hello.atlassian.net',
  cloudId: 'a436116f-02ce-4520-8fbb-7301462a1674',
  widestUsage: CustomerImpact.ATLASSIAN,
} satisfies Site;

export const pugSite = {
  hostname: 'pug.jira-dev.com',
  cloudId: 'DUMMY-a5a01d21-1cc3-4f29-9565-f2bb8cd969f5',
  widestUsage: CustomerImpact.ATLASSIAN,
} satisfies Site;

/**
 * Note: All Confluence products on HAMS have been migrated to CCP!
 * @see https://atlassian.slack.com/archives/C028JJ8LSFJ/p1762389873589029
 */
const ccpMigratedFromHamsManualTestingFreeSiteStaging = {
  hostname: 'atl-fabric-free-stg-hams-site.jira-dev.com',
  cloudId: 'f43c2adf-0510-4e34-876a-e3e30945551e',
  /**
   * We use this one in branch deploys for manual testing
   */
  meantForAutomatedTesting: false,
};

/**
 * Note: All Confluence products on HAMS have been migrated to CCP!
 * @see https://atlassian.slack.com/archives/C028JJ8LSFJ/p1762389873589029
 */
const ccpMigratedFromHamsPaywallFreeSiteStaging = {
  hostname: 'atl-fabric-free-stg-hams-paywall.jira-dev.com',
  cloudId: '7a70aa52-cabe-49ad-a16e-2b6070ee0df0',
  meantForAutomatedTesting: true,
} satisfies Site;

/**
 * Note: All Confluence products on HAMS have been migrated to CCP!
 * @see https://atlassian.slack.com/archives/C028JJ8LSFJ/p1762389873589029
 */
const ccpMigratedFromHamsFreeSitesStaging = [
  {
    hostname: 'atl-fabric-free-stg-hams-post-2.jira-dev.com',
    cloudId: 'e4716818-0f2f-423a-9890-8e75d16665f5',
  },
  {
    hostname: 'atl-fabric-free-stg-hams-post-3.jira-dev.com',
    cloudId: '5c1e87de-6dab-466e-af3b-7c2beb3f2a2d',
  },
  {
    hostname: 'atl-fabric-free-stg-hams-post-4.jira-dev.com',
    cloudId: 'ecdb70ba-c4e7-43e9-adcc-76c04e3a3c23',
    /**
     * For now, this site is on standard.
     * @see https://atlassian.slack.com/archives/CFJT0S2TY/p1762236425263539
     */
    unused: true,
  },
  {
    hostname: 'atl-fabric-free-stg-hams-post-5.jira-dev.com',
    cloudId: '80351849-5493-45de-adcb-5f90ba13e7c7',
  },
  {
    hostname: 'atl-fabric-free-stg-hams.jira-dev.com',
    cloudId: '835d0e64-6a7d-46f3-939f-08159597d05d',
  },
  ccpMigratedFromHamsManualTestingFreeSiteStaging,
  ccpMigratedFromHamsPaywallFreeSiteStaging,
].map((data) => ({ meantForAutomatedTesting: true, ...data })) satisfies Site[];

/**
 * Note: All Confluence products on HAMS have been migrated to CCP!
 * @see https://atlassian.slack.com/archives/C028JJ8LSFJ/p1762389873589029
 */
const ccpMigratedFromHamsPaywallFreeSiteProduction = {
  hostname: 'atl-fabric-free-prod-hams-paywall.atlassian.net',
  cloudId: 'e6d0c9dc-4c11-4399-bfb9-f4fd07a83b4d',
  meantForAutomatedTesting: true,
  testConfig: {
    fixedSpace: {
      key: 'FIXEDSPACE',
      id: '167837977',
    },
  },
} satisfies Site;

export const ccpManualTestingFreeSiteStaging = {
  hostname: 'atl-fabric-free-stg-ccp-post-3.jira-dev.com',
  cloudId: 'b8a161ad-9ede-472d-ad36-9edb1cc5c819',
  /**
   * We use this one in branch deploys for manual testing
   */
  meantForAutomatedTesting: false,
};

export const ccpFreeSitesStaging = (
  [
    ...ccpMigratedFromHamsFreeSitesStaging,
    ccpManualTestingFreeSiteStaging,
    {
      hostname: 'atl-fabric-free-stg-ccp-post-4.jira-dev.com',
      cloudId: 'df7f7fc9-da5e-49a1-8175-e3566aa09190',
    },
    // Removing as per https://atlassian.slack.com/archives/C027MLUFSFN/p1763687117509299
    // {
    //   hostname: 'atl-fabric-free-stg-ccp-post-5.jira-dev.com',
    //   cloudId: '1a5a5ff1-60fa-4205-b603-3e5743501acc',
    // },
    {
      hostname: 'atl-fabric-free-stg-ccp-post-6.jira-dev.com',
      cloudId: '3eaeda37-90a4-4282-961c-7aea764a6866',
    },
    // Temporarily removed as this site was throwing errors, and doesn't seem needed
    // {
    //   hostname: 'atl-fabric-free-stg-ccp-3.jira-dev.com',
    //   cloudId: 'a4afea8a-5a08-4a8b-b152-36def5028465',
    // },
  ] satisfies Site[]
).map((data) => ({ meantForAutomatedTesting: true, ...data }));

/**
 * Note: All Confluence products on HAMS have been migrated to CCP!
 * @see https://atlassian.slack.com/archives/C028JJ8LSFJ/p1762389873589029
 */
export const ccpMigratedFromHamsNonAdminStgSite = {
  hostname: 'atl-fabric-standard-stg-hams-post-non-admin.jira-dev.com',
  cloudId: '564847d0-3769-45d1-a760-d933197f4c4b',
  /**
   * TODO {@link https://product-fabric.atlassian.net/browse/CNS-18043}
   *
   * Currently suspended. Remove unused once this site is no longer suspended
   * or a new equivalent site has been created.
   */
  unused: true,
} satisfies Site;

/**
 * Note: All Confluence products on HAMS have been migrated to CCP!
 * @see https://atlassian.slack.com/archives/C028JJ8LSFJ/p1762389873589029
 */
const ccpMigratedFromHamsStandardSitesStaging = [
  {
    hostname: 'atl-fabric-standard-stg-hams-post.jira-dev.com',
    cloudId: '30eb9944-8420-4a4e-bd3c-ee97d342f19a',
  },
  {
    hostname: 'atl-fabric-standard-stg-hams-post-2.jira-dev.com',
    cloudId: 'f5c36c68-293b-4197-bd90-9bb21cdc8939',
  },
  {
    hostname: 'atl-fabric-standard-stg-hams-post-5.jira-dev.com',
    cloudId: 'e45ef192-f7cf-4b0c-b579-b08d4b1c4e41',
  },
  {
    hostname: 'atl-fabric-standard-stg-hams-post-6.jira-dev.com',
    cloudId: 'ccb836b1-a467-47c3-9663-78a4096be3df',
  },
  {
    hostname: 'atl-fabric-standard-stg-hams.jira-dev.com',
    cloudId: 'ce1605d4-f52c-4c4d-9c86-7421b6450137',
    // Suspended and superseded by -post sites
    unused: true,
  },
  ccpMigratedFromHamsNonAdminStgSite,
].map((data) => ({ meantForAutomatedTesting: true, ...data })) satisfies Site[];

/**
 * Note: All Confluence products on HAMS have been migrated to CCP!
 * @see https://atlassian.slack.com/archives/C028JJ8LSFJ/p1762389873589029
 */
const ccpMigratedFromHamsFreeSitesProduction = [
  {
    hostname: 'atl-fabric-free-prod-hams-post-2.atlassian.net',
    cloudId: '03d6d989-78de-4f6e-8e06-1287a4f6dea0',
    /**
     * This got upgraded to standard at some point.
     *
     * We have to wait until
     * {@link https://atlassian.slack.com/archives/CFJT0S2TY/p1762236425263539}
     * to mark this as used again
     *
     * TODO: Remove this {@link https://product-fabric.atlassian.net/browse/CNS-21318}
     */
    unused: true,
  },
  {
    hostname: 'atl-fabric-free-prod-hams-post-3.atlassian.net',
    cloudId: '8b003e9c-9cb9-413f-a9ce-b47bf4df1952',
    /**
     * This got upgraded to standard at some point.
     *
     * We have to wait until
     * {@link https://atlassian.slack.com/archives/CFJT0S2TY/p1762236425263539}
     * to mark this as used again
     *
     * TODO: Remove this {@link https://product-fabric.atlassian.net/browse/CNS-21318}
     */
    unused: true,
  },
  {
    hostname: 'atl-fabric-free-prod-hams-post-4.atlassian.net',
    cloudId: '796eceed-86e2-44b5-b55a-723dc9ffe7a2',
    /**
     * This got upgraded to standard at some point.
     *
     * We have to wait until
     * {@link https://atlassian.slack.com/archives/CFJT0S2TY/p1762236425263539}
     * to mark this as used again
     *
     * TODO: Remove this {@link https://product-fabric.atlassian.net/browse/CNS-21318}
     */
    unused: true,
  },
  {
    hostname: 'atl-fabric-free-prod-hams-post-5.atlassian.net',
    cloudId: 'ce96d1d5-e8ac-4c3d-a95b-35f4e163c296',
    /**
     * This got upgraded to standard at some point.
     *
     * We have to wait until
     * {@link https://atlassian.slack.com/archives/CFJT0S2TY/p1762236425263539}
     * to mark this as used again
     *
     * TODO: Remove this {@link https://product-fabric.atlassian.net/browse/CNS-21318}
     */
    unused: true,
  },
  {
    hostname: 'atl-fabric-free-prod-hams-post.atlassian.net',
    cloudId: '589a65b6-1bf2-4441-ab30-73ebe26da1be',
    // Suspended and superseded by the post-x sites
    unused: true,
  },
  {
    hostname: 'atl-fabric-free-prod-hams.atlassian.net',
    cloudId: '899464ae-5fc9-4509-8e39-405d7fd834e4',
    // Suspended and superseded by the post-x sites
    unused: true,
  },
  ccpMigratedFromHamsPaywallFreeSiteProduction,
].map((data) => ({ meantForAutomatedTesting: true, ...data })) satisfies Site[];

export const ccpFreeSitesProduction = [
  {
    hostname: 'atl-fabric-free-prod-ccp-post.atlassian.net',
    cloudId: '679df76d-60ce-4655-851a-595eceba1fa7',
  },
  {
    hostname: 'atl-fabric-free-prod-ccp-post-2.atlassian.net',
    cloudId: '89fbe81f-9856-4456-b6f4-3c7b2869926b',
  },
  {
    hostname: 'atl-fabric-free-prod-ccp-post-3.atlassian.net',
    cloudId: '04a5c370-a66a-42fa-9782-b7782cb7a184',
  },
  {
    hostname: 'atl-fabric-free-prod-ccp-post-4.atlassian.net',
    cloudId: 'f9019ec2-9b70-4cb2-bce3-8e9a711bf1b6',
  },
  ...ccpMigratedFromHamsFreeSitesProduction,
].map((data) => ({ meantForAutomatedTesting: true, ...data })) satisfies Site[];

/**
 * Note: All Confluence products on HAMS have been migrated to CCP!
 * @see https://atlassian.slack.com/archives/C028JJ8LSFJ/p1762389873589029
 */
const ccpMigratedFromHamsStandardSitesProduction = [
  {
    hostname: 'atl-fabric-standard-prod-hams-post.atlassian.net',
    cloudId: '8367ed66-c82a-45ef-9fa1-b1f7318ba50b',
  },
  {
    hostname: 'atl-fabric-standard-prod-hams-post-2.atlassian.net',
    cloudId: '628b5002-e83b-41d0-a5a5-633462342aaf',
  },
  {
    hostname: 'atl-fabric-standard-prod-hams-post-3.atlassian.net',
    cloudId: 'bf9bd1d0-c02a-41b1-8129-773f2b4d12c5',
  },
  {
    hostname: 'atl-fabric-standard-prod-hams-post-4.atlassian.net',
    cloudId: 'ffb36fd1-89b4-4f91-88ac-8411b6997d78',
  },
  {
    hostname: 'atl-fabric-standard-prod-hams.atlassian.net',
    cloudId: 'b58f599c-57fc-4c64-ac3d-8a00e32b4a5e',
  },
].map((data) => ({ meantForAutomatedTesting: true, ...data })) satisfies Site[];

export const ccpStandardSitesStaging = [
  {
    hostname: 'atl-fabric-standard-stg-ccp.jira-dev.com',
    cloudId: 'c3dca143-aa42-47b8-83e7-ddf1d70ef347',
    // Suspended, luckily, at the moment, we don't have a test using CCP
    // standard (would be ideal if we did though)
    unused: true,
  },
  {
    hostname: 'atl-fabric-standard-stg-ccp-post2.jira-dev.com',
    cloudId: 'c16160c0-c278-4ea8-b8ac-8db99c7d32a6',
    // Suspended, luckily, at the moment, we don't have a test using CCP
    // standard (would be ideal if we did though)
    unused: true,
  },
  ...ccpMigratedFromHamsStandardSitesStaging,
].map((data) => ({ meantForAutomatedTesting: true, ...data })) satisfies Site[];

export const ccpStandardSitesProduction = [
  {
    hostname: 'atl-fabric-standard-prod-ccp-post.atlassian.net',
    cloudId: 'ac78637d-e34d-4224-b592-5b6adc1dcb88',
  },
  {
    hostname: 'atl-fabric-standard-prod-ccp.atlassian.net',
    cloudId: 'fd3f1e69-4df6-411e-a390-f1acb2cc6b0e',
  },
  ...ccpMigratedFromHamsStandardSitesProduction,
].map((data) => ({ meantForAutomatedTesting: true, ...data })) satisfies Site[];

export const defaultStagingTestingSite = {
  hostname: 'canvas-testing-stg.jira-dev.com',
  cloudId: 'b29aa1b5-ec08-479c-8fd6-b569c8e28ef7',
  meantForAutomatedTesting: true,
  testConfig: {
    fixedSpace: {
      key: 'TEXTFIXED',
      id: '229380',
    },
  },
} satisfies Site;

export const defaultProductionTestingSite = {
  hostname: 'atl-vertigo--shard-conf-prod-us-preprod--canvas-2.atlassian.net',
  cloudId: '6ae5f3c4-235d-461e-b29f-b0fdb7273981',
  meantForAutomatedTesting: true,
  testConfig: {
    fixedSpace: {
      key: 'TEXTFIXED',
      id: '655365',
    },
  },
} satisfies Site;

/**
 * Note: All Confluence products on HAMS have been migrated to CCP!
 * @see https://atlassian.slack.com/archives/C028JJ8LSFJ/p1762389873589029
 */
const ccpMigratedFromHamsPremiumSitesStaging = [
  {
    // Consistently getting
    // "{"statusCode":500,"message":"org.springframework.transaction.UnexpectedRollbackException:
    // Transaction rolled back because it has been marked as rollback-only"}" on
    // this site whenever we try create a space
    unused: true,
    hostname: 'atl-fabric-premium-stg-hams.jira-dev.com',
    cloudId: '78fa71b0-1f1d-44f5-aad7-d5ab438eb53e',
  },
  {
    // Seeing that Confluence isn't even showing up in this site at the moment.
    // Marking unused to unblock main.
    hostname: 'atl-fabric-premium-stg-hams-post.jira-dev.com',
    cloudId: '5ba39301-2ee9-4e60-887b-e363f9b4ae06',
  },
].map((data) => ({ meantForAutomatedTesting: true, ...data })) satisfies Site[];

export const ccpPremiumSitesStaging = [
  {
    hostname: 'atl-fabric-premium-stg-ccp.jira-dev.com',
    cloudId: 'd1f0859b-41af-46e6-9184-b1290c8b4a32',
    // Suspended, luckily, at the moment, we don't have a test using CCP
    // premium (would be ideal if we did though)
    unused: true,
  },
  {
    hostname: 'atl-fabric-premium-stg-ccp-post2.jira-dev.com',
    cloudId: 'd7db70c8-9d75-426e-af36-39e7f6194634',
    // TODO https://product-fabric.atlassian.net/browse/CNS-18065
    // Suspended, luckily, at the moment, we don't have a test using CCP
    // premium (would be ideal if we did though)
    unused: true,
  },
  ...ccpMigratedFromHamsPremiumSitesStaging,
  defaultStagingTestingSite,
].map((data) => ({ meantForAutomatedTesting: true, ...data })) satisfies Site[];

/**
 * Note: All Confluence products on HAMS have been migrated to CCP!
 * @see https://atlassian.slack.com/archives/C028JJ8LSFJ/p1762389873589029
 */
const ccpMigratedFromHamsPremiumSitesProduction = [
  {
    hostname: 'atl-fabric-premium-prod-hams.atlassian.net',
    cloudId: '07091ff6-51b1-4e2b-b4b7-c62b07655885',
  },
  {
    hostname: 'atl-fabric-premium-prod-hams-post.atlassian.net',
    cloudId: '70738083-9588-4515-9561-ee0dc7436a64',
  },
].map((data) => ({ meantForAutomatedTesting: true, ...data })) satisfies Site[];

export const ccpPremiumSitesProduction = [
  {
    hostname: 'atl-fabric-premium-prod-ccp.atlassian.net',
    cloudId: '2855216c-8de3-494c-add5-7ad28e25642e',
  },
  {
    hostname: 'atl-fabric-premium-prod-ccp-post.atlassian.net',
    cloudId: 'a9faf70f-cf39-4e29-9be0-1a4a9a550218',
  },
  defaultProductionTestingSite,
  ...ccpMigratedFromHamsPremiumSitesProduction,
].map((data) => ({ meantForAutomatedTesting: true, ...data })) satisfies Site[];

export const fabricInConfluenceStaging = {
  hostname: 'fabric-in-confluence.jira-dev.com',
  cloudId: 'b5b906eb-2bb0-4c53-b9da-b21685f952ab',
  /**
   * We use this one for branch deploy manual testing
   */
  meantForAutomatedTesting: false,
};

export const fabricInConfluenceProduction = {
  hostname: 'fabric-in-confluence.atlassian.net',
  cloudId: 'eb0e8137-863c-410e-b012-5a19ad5196a4',
  /**
   * We use this one for branch deploy manual testing
   */
  meantForAutomatedTesting: false,
};

export const internalSitesWeOwn: Site[] = [
  ...ccpFreeSitesStaging,
  ...ccpStandardSitesStaging,
  ...ccpPremiumSitesStaging,

  ...ccpFreeSitesProduction,
  ...ccpStandardSitesProduction,
  ...ccpPremiumSitesProduction,

  // Access has been removed
  {
    hostname: 'canvas-development.atlassian.net',
    cloudId: '09b342d9-ac7a-4709-9d9f-80d1d99f36f9',
    standalone: true,
  },
  {
    hostname: 'atl-vertigo--shard-conf-prod-us-preprod--canvas.atlassian.net',
    cloudId: '1500ee6b-f234-41e1-b52c-ec3b0f4819f6',
    meantForAutomatedTesting: true,
  },
  fabricInConfluenceProduction,
  fabricInConfluenceStaging,
  {
    hostname: 'fabric-dare.jira-dev.com',
    cloudId: 'b817aaf3-da8a-49a5-aa26-609e4ff9621b',
  },
  {
    hostname: 'fabric-restrictions.jira-dev.com',
    cloudId: 'f7fcdc63-70d1-4f9a-8987-00242c92f260',
    // Failing at the moment so marked as standalone to avoid HTTP errors
    standalone: true,
  },
  {
    hostname: 'canvas.dev.atlassian.com',
    cloudId: '382e7407-d3b8-4b14-970b-d209d87ee26c',
    standalone: true,
  },
];
